import { useState } from 'react'

import {
  Box,
  Button,
  Callout,
  Icon,
  Spinner,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  EmployeesGrossSalaryHoursHoursInput,
  SalaryCalculatorGrossSalariesDocument,
  SalaryCalculatorInfoForClassDocument,
} from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import Modal from 'modules/core/components/Modal'
import MoneyField from 'modules/core/components/MoneyField'
import TextField from 'modules/core/components/TextField'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useCurrentAccessLegalEntity } from 'modules/trainings/hooks/Trainings/useCurrentAccessLegalEntity'

// Utility class to generate the hours in the needed format
// to query the gross salary from the sessions dates and durations
export class SalaryCostInfo {
  data: Map<string, number> = new Map()
  employees: number[] = []

  constructor(employees: number[]) {
    this.employees = employees
  }

  appendFromBackend(date: string, duration: number): void {
    if (date && duration) {
      //ignore sessions without date or duration
      const backendDate = dateBackendDateTimeToBackend(date)
      const current_duration = this.data.get(backendDate) || 0
      this.data.set(backendDate, current_duration + duration)
    }
  }

  hours(): EmployeesGrossSalaryHoursHoursInput[] {
    const data: Array<EmployeesGrossSalaryHoursHoursInput> = []
    this.data.forEach((hours, date) => {
      data.push({ date: date, hours: String(hours) })
    })

    return data
  }

  valid(): boolean {
    return this.employees.length > 0 && this.data.size > 0
  }

  error(): string {
    if (!this.employees.length) {
      return i18n.t('trainings.salary_cost_calculator.error_employees')
    }
    if (!this.data.size) {
      return i18n.t('trainings.salary_cost_calculator.error_sessions')
    }
    return ''
  }
}

const SalaryCostCalculatorModal = ({
  salaryCostInfo,
  classId,
  onApply,
  onClose,
  trackingSelectedFrom,
}: {
  salaryCostInfo?: SalaryCostInfo
  onApply: (value: string) => void
  onClose: () => void
  classId?: number
  trackingSelectedFrom: string
}) => {
  const [amount, setAmount] = useState<string>('default')

  // If we don't have 'classId' we are creating a new class, data is in 'salaryCostInfo'
  const { data: queryData, loading: loadingData } = useQuery(SalaryCalculatorInfoForClassDocument, {
    variables: {
      trainingClassId: Number(classId),
      trainingClassIds: [String(classId)],
    },
    skip: !classId,
  })

  // If we have 'classId' we are getting information for an existing class, we have to load participants
  // and hours once the data is loaded
  if (classId && !loadingData) {
    const employees = queryData?.trainings.trainingClassMemberships
      .map((membership) => Number(membership.access?.employee?.id) || 0)
      .filter(function (v) {
        return !!v
      })
    salaryCostInfo = new SalaryCostInfo(employees || [])
    queryData?.trainings.sessions.forEach((session) => {
      salaryCostInfo?.appendFromBackend(session.startsAt, Number(session.duration) || 0)
    })
  }

  // if we have 'salaryCostInfo' ('loadingData' is false if we have 'salaryCostInfo' or data loading has finished)
  // make a request to load the gross salaries
  const { data, loading: loadingSalary } = useQuery(SalaryCalculatorGrossSalariesDocument, {
    variables: {
      employeeIds: salaryCostInfo?.employees || [],
      hours: salaryCostInfo?.hours() || [],
    },
    skip: loadingData || !salaryCostInfo?.valid(),
  })

  let total: number | undefined = undefined
  if (data) {
    total = data.trainings.employeesGrossSalaryHours.reduce(
      (accumulator, EmployeeGrossSalaryHours) =>
        accumulator + EmployeeGrossSalaryHours.totalSalaryInCents,
      0
    )
    if (amount === 'default') {
      setAmount(String(total / 100))
    }
  }

  const handleAmountChange = (value: string) => {
    setAmount(value)
  }

  const handleSubmit = () => {
    track(trackingEvents.training.salaryCostCalculator.apply, {
      selected_from: trackingSelectedFrom,
    })
    onApply(amount)
  }

  const { legalEntity, loading: loadingEntity } = useCurrentAccessLegalEntity()
  const loading = loadingData || loadingSalary || loadingEntity
  const [open, toggle] = useToggle(false)

  return (
    <Modal
      title={i18n.t('trainings.salary_cost_calculator.title')}
      illustration="calculator"
      onSubmit={handleSubmit}
      onClose={onClose}
      footer={
        <Button submit disabled={loading || !salaryCostInfo?.valid()}>
          {i18n.t('common.apply')}
        </Button>
      }
    >
      <Box gap="s16">
        <LabeledInput
          isLegend
          label={i18n.t('trainings.salary_cost_calculator.field_title')}
          secondaryLabel={
            <Tooltip.Alt
              attachment="bottom center"
              open={open}
              toggle={toggle}
              label={i18n.t('trainings.salary_cost_calculator.field_hint')}
            >
              <Icon.Medium icon="info-circle-line" color="grey800" />
            </Tooltip.Alt>
          }
        >
          {loading && <TextField placeholder="0.00" indicator={<Spinner></Spinner>} />}
          {!loading && (
            <MoneyField
              currency={legalEntity?.currency || ''}
              placeholder="0.00"
              onChange={handleAmountChange}
              value={amount === 'default' ? '' : amount}
              maxDecimals={2}
              maxLength={6}
            />
          )}
        </LabeledInput>
        {salaryCostInfo && !salaryCostInfo.valid() ? (
          <Callout type="warning">{salaryCostInfo?.error()}</Callout>
        ) : (
          <Callout type="info">{i18n.t('trainings.salary_cost_calculator.callout')}</Callout>
        )}
      </Box>
    </Modal>
  )
}

interface BaseProps {
  // A function that returns true to close the calculator or
  // false to remain open after applying the calculated value
  onApply: (value: string) => boolean
  trackingSelectedFrom: string
}

// when creating a group, we have the sessions duration
// and participants in memory. Use salaryCostInfo parameter
interface SalaryCostCalculatorInfo extends BaseProps {
  salaryCostInfo: SalaryCostInfo
  classId?: never
}

// when editing a group, we make a request to get sessions duration
// and participants. Use classId parameter
interface SalaryCostCalculatorClassId extends BaseProps {
  salaryCostInfo?: never
  classId: number
}

type SalaryCostCalculatorProps = SalaryCostCalculatorInfo | SalaryCostCalculatorClassId

export const SalaryCostCalculator = ({
  onApply,
  trackingSelectedFrom,
  salaryCostInfo,
  classId,
}: SalaryCostCalculatorProps) => {
  const [calculatorOpen, setCalculatorOpen] = useState(false)
  const [isTooltipOpen, toggleTooltip] = useToggle(false)

  const isCalculatorDisabled = (): boolean => {
    return Boolean(salaryCostInfo && !salaryCostInfo.valid())
  }

  const showCalculator = () => {
    track(trackingEvents.training.salaryCostCalculator.calculate, {
      selected_from: trackingSelectedFrom,
    })
    setCalculatorOpen(true)
  }

  return (
    <Box onMouseEnter={toggleTooltip(true)} onMouseLeave={toggleTooltip(false)}>
      <Tooltip.Alt
        open={isTooltipOpen && isCalculatorDisabled()}
        attachment="top center"
        label={salaryCostInfo?.error()}
      >
        <Button
          type="secondary"
          iconName="calculator"
          onClick={showCalculator}
          disabled={isCalculatorDisabled()}
        >
          {i18n.t('trainings.salary_cost_calculator.button')}
        </Button>
      </Tooltip.Alt>
      {calculatorOpen && (
        <SalaryCostCalculatorModal
          salaryCostInfo={salaryCostInfo}
          onApply={(value: string) => {
            if (onApply(value)) {
              setCalculatorOpen(false)
            }
          }}
          onClose={() => setCalculatorOpen(false)}
          classId={classId}
          trackingSelectedFrom={trackingSelectedFrom}
        />
      )}
    </Box>
  )
}
